import { useCallback, useEffect, useMemo, type MutableRefObject } from "react";
import { fetchFacebookMessagesDetail, fetchZaloMessagesDetail } from "@features/messages/messagesSlice";
import { CHANNELS } from "@common";
import type { Message } from "@interfaces/chat.interface";
import type { AppDispatch } from "@stores/redux/store";

type MessagesStore = {
  messagesByConversation: Record<string, Message[]>;
  loading: Record<string, boolean>;
  meta: Record<string, { total: number; offset: number; limit: number } | null>;
};

type Conversation = {
  id: string;
  channel: string;
  fanpageName?: string;
  unreadCount?: number;
};

type Page = { id: string; name: string; messageSyncStatus?: string };
type OA = { id: string; name: string };

type Params = {
  activeConversation?: Conversation;
  selectedConversationId?: string;
  selectedFanpageName?: string;
  selectedChannel: string;
  facebookPages: Page[];
  zaloOAs: OA[];
  messagesStore: MessagesStore;
  dispatch: AppDispatch;
  markConversationAsRead: (conversationId: string) => void;
  urlConversationId?: string;
  hasClearedNotSyncedRef: MutableRefObject<boolean>;
};

export const useConversationMessages = ({
  activeConversation,
  selectedConversationId,
  selectedFanpageName,
  selectedChannel,
  facebookPages,
  zaloOAs,
  messagesStore,
  dispatch,
  markConversationAsRead,
  urlConversationId,
  hasClearedNotSyncedRef,
}: Params) => {
  const currentConversationId = useMemo(() => {
    if (!selectedConversationId) {
      return null;
    }
    if (activeConversation?.id) {
      return activeConversation.id;
    }
    return null;
  }, [selectedConversationId, activeConversation]);

  const currentMessages = useMemo(() => {
    if (!currentConversationId) return [];
    return messagesStore.messagesByConversation[currentConversationId] || [];
  }, [currentConversationId, messagesStore.messagesByConversation]);

  const isLoading = useMemo(() => {
    if (!currentConversationId) return false;
    return messagesStore.loading[currentConversationId] || false;
  }, [currentConversationId, messagesStore.loading]);

  const conversationMeta = useMemo(() => {
    if (!currentConversationId) return null;
    return messagesStore.meta[currentConversationId] || null;
  }, [currentConversationId, messagesStore.meta]);

  const hasMore = useMemo(() => {
    if (!conversationMeta) return false;
    return currentMessages.length < conversationMeta.total;
  }, [conversationMeta, currentMessages.length]);

  const handleLoadMore = useCallback(() => {
    if (!currentConversationId || !hasMore || isLoading) return;

    const selectedPage = facebookPages.find(
      (page) => page.name === selectedFanpageName
    );
    if (!selectedPage) return;

    const currentOffset = conversationMeta?.offset || 0;
    const currentLimit = conversationMeta?.limit || 100;
    const nextOffset = currentOffset + currentLimit;

    dispatch(
      fetchFacebookMessagesDetail({
        conversationId: currentConversationId,
        pageId: selectedPage.id,
        offset: nextOffset,
        limit: currentLimit,
      })
    );
  }, [
    currentConversationId,
    hasMore,
    isLoading,
    selectedFanpageName,
    facebookPages,
    conversationMeta,
    dispatch,
  ]);

  useEffect(() => {
    if (
      selectedConversationId &&
      selectedFanpageName &&
      activeConversation
    ) {
      if (activeConversation.channel !== selectedChannel) {
        return;
      }
      if (
        activeConversation.fanpageName &&
        activeConversation.fanpageName !== selectedFanpageName
      ) {
        return;
      }

      const controller = new AbortController();
      const signal = controller.signal;

      // Handle Zalo messages
      if (selectedChannel === CHANNELS.ZALO) {
        const selectedOA = zaloOAs.find((oa) => oa.name === selectedFanpageName);
        
        if (selectedOA) {
          const messagesAlreadyLoaded =
            messagesStore.messagesByConversation[selectedConversationId]?.length > 0;
          const isLoading = messagesStore.loading[selectedConversationId] === true;

          // Only fetch if messages are not loaded and not currently loading
          if (!messagesAlreadyLoaded && !isLoading) {
            dispatch(
              fetchZaloMessagesDetail({
                conversationId: selectedConversationId,
                offset: 0,
                limit: 100,
                signal,
              })
            );
          }
        }
      }

      // Handle Facebook messages
      if (selectedChannel === CHANNELS.FACEBOOK && facebookPages.length > 0) {
        const selectedPage = facebookPages.find(
          (page) => page.name === selectedFanpageName
        );

        if (selectedPage && selectedPage.messageSyncStatus === "NOT_SYNCED") {
          if (hasClearedNotSyncedRef.current) {
            hasClearedNotSyncedRef.current = false;
          }
        }

        if (selectedPage) {
          const messagesAlreadyLoaded =
            messagesStore.messagesByConversation[selectedConversationId]?.length > 0;
          const isLoading = messagesStore.loading[selectedConversationId] === true;

          // Only fetch if messages are not loaded and not currently loading
          if (!messagesAlreadyLoaded && !isLoading) {
            dispatch(
              fetchFacebookMessagesDetail({
                conversationId: selectedConversationId,
                pageId: selectedPage.id,
                offset: 0,
                limit: 100,
                signal,
              })
            );
          }
        }
      }

      return () => {
        controller.abort();
      };
    }
  }, [
    selectedConversationId,
    selectedFanpageName,
    selectedChannel,
    activeConversation,
    facebookPages,
    zaloOAs,
    messagesStore.messagesByConversation,
    messagesStore.loading,
    dispatch,
    hasClearedNotSyncedRef,
  ]);

  useEffect(() => {
    const conversationIdToCheck = urlConversationId || selectedConversationId;
    if (conversationIdToCheck && activeConversation) {
      if ((activeConversation.unreadCount ?? 0) > 0) {
        markConversationAsRead(conversationIdToCheck);
      }
    }
  }, [
    urlConversationId,
    selectedConversationId,
    activeConversation,
    markConversationAsRead,
  ]);

  return {
    currentConversationId,
    currentMessages,
    isLoading,
    conversationMeta,
    hasMore,
    handleLoadMore,
  };
};
